import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  Typography,
  TextField,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useRoles } from "../../context/AuthenticationTokenContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faFaceAngry,
  faGlobe,
  faUserDoctor,
} from "@fortawesome/free-solid-svg-icons";
import { faTrashCheck } from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { getNoteRole, status } from "shared/src/appointment.mjs";
import * as api from "../../services/api";
import * as f from "../../utils/formatter";
import IconSeat from "../icons/IconSeat.jsx";
import IconNotes from "../icons/IconNotes.jsx";
import { useLatest } from "../../hooks";
import { formData, FormErrorList, useFormAction } from "../../form";
import ButtonLoader from "../ButtonLoader";
import {
  getCompoundDoctor,
  rawWaitingTimes,
  waitingTimesIcons,
} from "../calendar";
import Link from "../Link";
import { role } from "shared/src/roles.mjs";
import ServiceTag from "../ServiceTag.jsx";
import { getForeignSpeakingOnlyTitle } from "shared/src/client.mjs";
import ProblematicTreatment from "../client/ProblematicTreatment";
import { useClientProblem } from "../client/hooks/useClientProblem.jsx";
import { ProductSelector } from "../ProductSelector";
import * as lctx from "../../context/LocationContext";
import { ServiceSelector } from "../ServiceSelector";
import { TREATMENT_STATE_INACTIVE } from "shared/src/company-tag.mjs";

function useDetail(appointment) {
  const client_id = appointment.client?.id;
  const [state, setState] = React.useState({
    loading: true,
  });

  useLatest(
    async (signal) => {
      setState((state) => ({ ...state, loading: true }));
      try {
        const res = await api.loadAppointmentDetail(
          {
            client_id: client_id,
            treatmentsFilter: {
              product_id: { eq: appointment.service?.product_id },
              state: { neq: TREATMENT_STATE_INACTIVE },
            },
            lastAppointmentFilter: {
              client_id: { eq: client_id },
              status_id: { in: [status.TREATED, status.FINISHED] },
            },
          },
          signal,
        );

        const lastAppointment = res.data.lastAppointments.data[0];
        const employeeAssignmentAudit =
          lastAppointment?.employeeAssignmentAudits?.data?.[0];

        setState((state) => ({
          ...state,
          data: {
            lastAppointment,
            employeeAssignmentAudit,
            client: res.data.client,
          },
        }));
      } finally {
        if (!signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [client_id],
  );

  return state;
}

function useEmployeeNameData(ids) {
  const [state, setState] = React.useState({
    loading: true,
    data: [],
  });

  useLatest(
    async (signal) => {
      try {
        setState((state) => ({ ...state, loading: true }));
        const { data } = await api.loadEmployeeNameData(ids);
        setState((state) => ({ ...state, data: data.employees.data }));
      } finally {
        if (!signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [ids],
  );

  return state;
}

function DetailProgress() {
  return <CircularProgress size={13} />;
}

function LastAppointment({ loading, lastAppointment }) {
  if (loading) {
    return <DetailProgress />;
  }

  if (lastAppointment == null) {
    return f.value(null);
  }

  return (
    <>
      {lastAppointment.service?.name}, {f.date(lastAppointment.start)}
    </>
  );
}

function crerateArray(...items) {
  return items.filter((item) => item != null);
}

function LastNurse({ employeeAssignmentAudit }) {
  const ids = React.useMemo(
    () => crerateArray(employeeAssignmentAudit?.changed_fields?.employee_id),
    [employeeAssignmentAudit],
  );
  const employees = useEmployeeNameData(ids);

  if (employees.loading) {
    return <DetailProgress />;
  }

  return f.value(employees.data.map((e) => f.fullName(e)));
}

function Doctor({ client }) {
  if (client.any_doctor) {
    return "Libovolný lékař";
  }

  return f.value(client?.doctors?.map((d) => f.fullName(d))?.join(", "));
}

function PlanLink({ client }) {
  const link = client?.treatments?.[0]?.plans?.[0]?.link;
  if (link == null) {
    return f.value(null);
  }

  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      Otevřít plán
    </a>
  );
}

/**
 * An appointment detail dialog.
 *
 * @typedef {object} AppointmentDetailDialogProps
 * @property {Appointment} appointment
 * @property {function} handleCloseAppointmentDetailDialog Function to close appointment detail dialog
 * @property {boolean} openAppointmentDetailDialog Flag to open appointment detail dialog
 *
 * @param {AppointmentDetailDialogProps} props
 * @returns {JSX.Element}
 */

export function AppointmentDetailDialog({
  appointmentList,
  handleCloseAppointmentDetailDialog,
  openAppointmentDetailDialog,
}) {
  const currentRoles = useRoles();

  const appointment = Array.isArray(appointmentList)
    ? appointmentList[0]
    : appointmentList; // TODO handle multiple appointments when ready
  const waitingTimes = waitingTimesIcons(
    rawWaitingTimes(appointment, new Date()),
  );
  const { t } = useTranslation();
  const canEditAppointment = currentRoles.has(role.Floor_Manager);
  const { loading, errors, onSubmit } = useFormAction(
    (e) => {
      const noteText = (textboxRef.current?.value || "").trim();
      const reqs = [];

      if (noteText) {
        const chosenRole = getNoteRole(roles);

        if (!chosenRole) {
          throw new Error(
            "Nemáte roli pro vytvoření poznámky k dalšímu termínu.",
          );
        }

        reqs.push(
          api.createNextAppointmentNote({
            input: {
              client_id: appointment.client.id,
              note: noteText,
              role: chosenRole,
            },
          }),
        );
      }

      reqs.push(api.patchAppointment(formData(e.target)));
      return Promise.all(reqs);
    },
    () => {
      handleCloseAppointmentDetailDialog();
    },
  );

  const { data: detail, loading: loadingDetail } = useDetail(appointment);

  const textboxRef = useRef(null);
  const roles = useRoles();
  const client = appointment.client ?? {};
  const problem = useClientProblem(client, roles);
  const languages = getForeignSpeakingOnlyTitle(client);
  const doctor = getCompoundDoctor([appointment]);

  const [notesState, setNotesState] = React.useState({
    loading: true,
    data: [],
  });
  useLatest(
    async (signal) => {
      try {
        setNotesState((s) => ({ ...s, loading: true }));
        const res = await api.nextAppointmentNotes(
          {
            client_id: { eq: String(client.id) },
            active: { eq: true },
          },

          { signal },
        );
        const list = res?.data?.nextAppointmentNotes?.data ?? [];

        setNotesState({ loading: false, data: list });
      } catch {
        if (!signal?.aborted) setNotesState({ loading: false, data: [] });
      }
    },
    [client.id],
  );

  const location = lctx.useCurrentLocation();
  const [productId, setProductId] = React.useState(
    appointment.service.product_id,
  );

  return (
    <Dialog
      open={!!openAppointmentDetailDialog}
      onClose={handleCloseAppointmentDetailDialog}
    >
      <DialogTitle
        sx={{
          padding: "2.4rem 2rem",
          margin: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {f.fullName(appointment.client)}
        <IconButton
          aria-label="close"
          onClick={handleCloseAppointmentDetailDialog}
        >
          <FontAwesomeIcon icon={faXmark} fixedWidth />
        </IconButton>
      </DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            padding: "0 3.8rem",
          }}
        >
          <input type="hidden" name="id" value={appointment.id} />
          <Box
            sx={{
              display: "flex",
              gap: "10px",
            }}
          >
            <ProblematicTreatment client={client} />

            <IconNotes appointment={appointment} />

            {languages != null && (
              <Tooltip arrow title={languages}>
                <FontAwesomeIcon
                  icon={faGlobe}
                  style={{ color: "var(--mui-palette-success-indicator)" }}
                />
              </Tooltip>
            )}

            {problem != null && (
              <Tooltip arrow title={problem}>
                <FontAwesomeIcon
                  icon={faFaceAngry}
                  style={{ color: "var(--mui-palette-error-indicator)" }}
                />
              </Tooltip>
            )}
            {doctor != null && (
              <Tooltip arrow title={doctor}>
                <FontAwesomeIcon
                  icon={faUserDoctor}
                  style={{ color: "var(--mui-palette-info-indicator)" }}
                />
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "3rem",
              alignItems: "center",
              fontSize: "1.5rem",
            }}
          >
            <span>
              <strong>{t("entity.client.birthdate")}: </strong>
              {f.date(client.birthdate)}
            </span>
            <span>
              <span>
                <strong>ID:</strong> {f.value(client.labs_id)}
              </span>
            </span>
          </Box>
          <Divider />

          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Typography variant="h3" sx={{ fontSize: "1.5rem" }}>
              Produkt:
            </Typography>
            <span>
              {loadingDetail ? (
                <DetailProgress />
              ) : (
                f.value(detail?.client?.treatments[0]?.product?.name)
              )}
            </span>
          </Box>
          <Box sx={{ display: "flex", gap: "1rem", marginBottom: "2rem" }}>
            <Typography variant="h3" sx={{ fontSize: "1.5rem" }}>
              Léčebný plán:
            </Typography>
            <span>
              {loadingDetail ? (
                <DetailProgress />
              ) : (
                <PlanLink client={detail.client} />
              )}
            </span>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: "1rem",
              flexDirection: "column",
            }}
          >
            <Typography variant="h3">Dnešní appointment</Typography>
            <Typography
              variant="h3"
              sx={{ fontSize: "1.5rem", color: "primary.main" }}
            >
              {f.longDateTime(appointment.start)}
            </Typography>
            {canEditAppointment && (
              <>
                <ProductSelector
                  company_id={location.company_id}
                  client_id={appointment.client.id}
                  required
                  onChange={(v) => setProductId(v?.id)}
                  default_product_id={productId}
                />
                <ServiceSelector
                  key={productId ?? ""}
                  location_id={location.id}
                  product_id={productId}
                  required
                  name="service_id"
                  default_service_id={appointment.service.id}
                  truthyFilters={["change_reservation_to"]}
                />
              </>
            )}
            {!canEditAppointment && (
              <ServiceTag service={appointment.service} />
            )}
          </Box>
          {appointment.status_id === status.ASSIGNED && (
            <Box
              sx={{
                color: "primary.main",
                fontWeight: "bold",
                fontSize: "1.3rem",
              }}
            >
              <IconSeat /> {t("reception.seat_preparing")}
            </Box>
          )}
          {(currentRoles.has(role.Front_Office) ||
            currentRoles.has(role.Back_Office) ||
            currentRoles.has(role.Manager_Customer_Care)) &&
            (appointment.status_id === status.BOOKED ||
              appointment.status_id === status.CONFIRMED ||
              appointment.status_id === status.WAITING) && (
              <Box sx={{ display: "flex", gap: "1rem" }}>
                {detail?.client?.treatments[0] != null && (
                  <Link
                    to={`/clients/${appointment.client.id}?reschedule=${appointment.parent_id ?? appointment.id}`}
                  >
                    <Button variant="outlined" style={{ fontSize: "1.4rem" }}>
                      <FontAwesomeIcon
                        icon={faCalendarDays}
                        style={{ marginRight: "8px" }}
                      />
                      Přeobjednat
                    </Button>
                  </Link>
                )}
                <Link
                  to={`/clients/${appointment.client.id}?delete=${appointment.parent_id ?? appointment.id}`}
                >
                  <Button variant="outlined">
                    <FontAwesomeIcon
                      icon={faTrashCheck}
                      style={{ marginRight: "8px" }}
                    />
                    Zrušit termín
                  </Button>
                </Link>
              </Box>
            )}

          <Box style={{ display: "flex", gap: "2rem" }}>
            {waitingTimes?.checked_in_minutes}
            {waitingTimes?.waiting_minutes}
          </Box>

          <Box sx={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
            {notesState.loading ? (
              <DetailProgress />
            ) : notesState.data.length > 0 ? (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
              >
                <Typography
                  component="div"
                  variant="h3"
                  sx={{ fontSize: "1.5rem" }}
                >
                  Poznámky k dnešnímu appointmentu:
                </Typography>

                {notesState.data.map((n) => (
                  <Typography
                    key={n.id}
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.4 }}
                  >
                    {f.shortDateTime(n.created_at)}
                    {": "}
                    {n.note}
                  </Typography>
                ))}
              </Box>
            ) : null}
            <span>
              <strong> {t("entity.client.risks")}:</strong>{" "}
              {f.value(client.risks)}
            </span>
            <span>
              <strong>{t("entity.client.medicaments")}:</strong>{" "}
              {f.value(client.medicaments)}
            </span>
            <span>
              <strong>Preferovaný lékař:</strong> <Doctor client={client} />
            </span>
            <span>
              <strong>Klinika:</strong> {f.value(client.location?.name)}
            </span>
            <span>
              <strong>Poslední ošetřující sestra:</strong>{" "}
              <LastNurse
                employeeAssignmentAudit={detail?.employeeAssignmentAudit}
              />
            </span>
            <span>
              <strong>Poslední app.:</strong>{" "}
              <LastAppointment
                loading={loadingDetail}
                lastAppointment={detail?.lastAppointment}
              />
            </span>
          </Box>

          <Box sx={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
            <span>
              <strong>Poznámka k termínu:</strong>
            </span>
            <TextField
              multiline
              rows={3}
              sx={{ flexGrow: 1 }}
              placeholder="Zde můžete napsat zprávu"
              slotProps={{ htmlInput: { ref: textboxRef } }}
            />
          </Box>

          <FormErrorList formErrors={errors} />
        </DialogContent>

        <DialogActions sx={{ padding: "3.5rem 2rem 3rem 2rem" }}>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => {
              handleCloseAppointmentDetailDialog();
            }}
            style={{ fontSize: "1.4rem" }}
          >
            Zrušit
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={loading}
            type="submit"
            style={{ fontSize: "1.4rem" }}
          >
            {loading && <ButtonLoader />}
            Uložit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
